/**
 * Workflows Hooks
 * Jotai atoms and hooks for workflow management
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { keepPreviousData } from '@tanstack/query-core';
import { workflowService } from '@/services';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type {
  CreateWorkflowDto,
  UpdateWorkflowDto,
  PatchSEGNodeDto,
  WorkflowType,
  CompilationStatus,
  Workflow,
  WorkflowSummary,
  PaginationMeta,
  VectorSyncResult
} from '@/types';
import { toast } from '@/components/ui/sonner';
import axios from 'axios';

type WorkflowListFilters = {
  status?: boolean;
  type?: WorkflowType;
  compilationStatus?: CompilationStatus;
  page?: number;
  limit?: number;
  search?: string;
  /** Workflows whose SEG calls this tool-server action (Integrations "used in workflows"). */
  tool?: string;
  provider?: string;
  category?: string;
  subcategory?: string;
  /** Only workflows with no category yet. */
  uncategorized?: boolean;
};

// Query atoms
const workflowsFilteredQueryAtom = (filters?: WorkflowListFilters, enabled = true) =>
  atomWithQuery((get) => ({
    queryKey: ['workflows', filters ?? {}, buScopeKey(get)],
    queryFn: () => workflowService.getWorkflows(filters, buScopeParams(get)),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled
  }));

// Every running workflow: the list answers at most 100 a page, so the pages are walked.
const runningWorkflowsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['workflows', 'running', 'all-pages', buScopeKey(get)],
  queryFn: async () => {
    const scope = buScopeParams(get);
    const first = await workflowService.getWorkflows({ status: true, page: 1, limit: 100 }, scope);
    // Page one reveals the count, so the rest are independent and go together.
    const rest = Array.from({ length: Math.max(0, first.pagination.totalPages - 1) }, (_, i) =>
      workflowService.getWorkflows({ status: true, page: i + 2, limit: 100 }, scope)
    );
    return [...first.workflows, ...(await Promise.all(rest)).flatMap((page) => page.workflows)];
  },
  staleTime: 30 * 1000
}));

const workflowByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['workflows', id],
    queryFn: () => workflowService.getWorkflowById(id),
    enabled: !!id,
    staleTime: 30 * 1000
  }));

// Mutation atoms
// Scoped like the query atoms: the options factory gets the same `get`, so the create
// carries whichever brand is selected. Without this the row lands shared (bu_id null) and
// the brand filter on every read matches it for every brand.
const createWorkflowMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['workflows', 'create'],
  mutationFn: (data: CreateWorkflowDto) => workflowService.createWorkflow(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['workflows']);
  }
}));

const updateWorkflowMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'update'],
  mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowDto }) => workflowService.updateWorkflow(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['workflows']);
    invalidateQueries(['workflows', variables.id]);
  }
}));

const toggleWorkflowStatusMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'toggle-status'],
  mutationFn: (id: string) => workflowService.toggleWorkflowStatus(id),
  onSuccess: (_data, id) => {
    invalidateQueries(['workflows']);
    invalidateQueries(['workflows', id]);
  }
}));

const deleteWorkflowMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'delete'],
  mutationFn: (id: string) => workflowService.deleteWorkflow(id),
  onSuccess: () => {
    invalidateQueries(['workflows']);
  }
}));

const patchSEGNodeMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'patch-seg-node'],
  mutationFn: ({ id, body }: { id: string; body: PatchSEGNodeDto }) => workflowService.patchSEGNode(id, body),
  onSuccess: (_data, variables) => {
    invalidateQueries(['workflows']);
    invalidateQueries(['workflows', variables.id]);
  }
}));

function patchWorkflowInCaches(id: string, patch: Partial<Workflow & WorkflowSummary>) {
  // List queries return { workflows, pagination }
  queryClient.setQueriesData<{ workflows: WorkflowSummary[]; pagination: PaginationMeta } | undefined>(
    { queryKey: ['workflows'] },
    (prev) => {
      if (!prev || !Array.isArray(prev.workflows)) return prev;
      let changed = false;
      const nextList = prev.workflows.map((w) => {
        if (w.id !== id) return w;
        changed = true;
        return { ...w, ...patch };
      });
      return changed ? { ...prev, workflows: nextList } : prev;
    }
  );
  // brand query returns Workflow[]
  queryClient.setQueriesData<Workflow[] | undefined>(
    { queryKey: ['workflows', 'brand'] },
    (prev) => {
      if (!Array.isArray(prev)) return prev;
      let changed = false;
      const next = prev.map((w) => {
        if (w.id !== id) return w;
        changed = true;
        return { ...w, ...patch };
      });
      return changed ? next : prev;
    }
  );
  queryClient.setQueryData<Workflow | undefined>(['workflows', id], (prev) =>
    prev ? { ...prev, ...patch } : prev
  );
}

const resyncWorkflowMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'resync'],
  mutationFn: ({ id, dryRun }: { id: string; dryRun?: boolean }): Promise<VectorSyncResult> =>
    workflowService.resyncWorkflow(id, dryRun),
  onMutate: async ({ id }: { id: string; dryRun?: boolean }) => {
    await queryClient.cancelQueries({ queryKey: ['workflows'] });
    const snapshots = queryClient.getQueriesData({ queryKey: ['workflows'] });
    patchWorkflowInCaches(id, { vectorSyncStatus: 'syncing' });
    return { snapshots };
  },
  onSuccess: (result: VectorSyncResult) => {
    if (result.status === 'synced') toast.success('Vector sync complete');
    else if (result.status === 'skipped') toast.message(`Skipped: ${result.reason ?? 'workflow no longer qualifies'}`);
    else if (result.status === 'failed') toast.error(result.error ?? 'Vector sync failed');
  },
  onError: (err: unknown, _variables, context: { snapshots: [readonly unknown[], unknown][] } | undefined) => {
    if (context?.snapshots) {
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    }
    let msg = 'Vector sync failed';
    if (axios.isAxiosError(err)) {
      const data = err.response?.data as { data?: { error?: string }; message?: string } | undefined;
      msg = data?.data?.error ?? data?.message ?? err.message ?? msg;
    } else if (err instanceof Error) {
      msg = err.message;
    }
    toast.error(msg);
  },
  onSettled: (_d, _e, variables) => {
    invalidateQueries(['workflows']);
    if (variables?.id) {
      queryClient.invalidateQueries({ queryKey: ['workflows', variables.id] });
    }
  }
}));

const updateWorkflowChannelsMutationAtom = atomWithMutation(() => ({
  mutationKey: ['workflows', 'update-channels'],
  mutationFn: ({ id, enabledChannels }: { id: string; enabledChannels: string[] }) => workflowService.updateWorkflowChannels(id, enabledChannels),
  onSuccess: (_data, variables) => {
    invalidateQueries(['workflows']);
    invalidateQueries(['workflows', variables.id]);
  }
}));

// Hooks
/** `enabled: false` holds the fetch until it is needed; the query key stays the same either way. */
export function useWorkflows(filters?: WorkflowListFilters, { enabled = true }: { enabled?: boolean } = {}) {
  const filterKey = JSON.stringify(filters ?? {});
  const atom = useMemo(() => workflowsFilteredQueryAtom(filters, enabled), [filterKey, enabled]);
  const [query] = useAtom(atom);
  return query;
}

export function useRunningWorkflows() {
  const [query] = useAtom(runningWorkflowsQueryAtom);
  return query;
}

export function useWorkflowById(id: string) {
  const atom = useMemo(() => workflowByIdQueryAtom(id), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useCreateWorkflow() {
  const [mutation] = useAtom(createWorkflowMutationAtom);
  return mutation;
}

export function useUpdateWorkflow() {
  const [mutation] = useAtom(updateWorkflowMutationAtom);
  return mutation;
}

export function useToggleWorkflowStatus() {
  const [mutation] = useAtom(toggleWorkflowStatusMutationAtom);
  return mutation;
}

export function useDeleteWorkflow() {
  const [mutation] = useAtom(deleteWorkflowMutationAtom);
  return mutation;
}

export function usePatchSEGNode() {
  const [mutation] = useAtom(patchSEGNodeMutationAtom);
  return mutation;
}

export function useUpdateWorkflowChannels() {
  const [mutation] = useAtom(updateWorkflowChannelsMutationAtom);
  return mutation;
}

export function useResyncWorkflow() {
  const [mutation] = useAtom(resyncWorkflowMutationAtom);
  return mutation;
}
